import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { Loading } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, dmyTime } from "@/lib/format";

const TABS = [["overview", "Overview"], ["batches", "Batches"], ["purchases", "Purchase history"],
  ["sales", "Sales history"], ["timeline", "Inventory timeline"]];

const Row = ({ label, value }) => (
  <div>
    <p className="overline text-slate-400">{label}</p>
    <p className="mt-1.5 text-sm text-slate-800 break-words">{value ?? "—"}</p>
  </div>
);

const emptyMove = () => ({ batch_id: "", batch_no: "", expiry_date: "", quantity: "",
  purchase_price: "", mrp: "", selling_price: "", gst_percent: "12", reason: "" });

export function MedicineDetail({ medicineId, onClose, onChanged, onEdit }) {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [move, setMove] = useState(null); // 'in' | 'out'
  const [form, setForm] = useState(emptyMove());
  const [confirm, setConfirm] = useState(null); // 'delete' | 'status' | 'archive' | 'archive-bulk'
  const [reason, setReason] = useState("");
  const [cloneName, setCloneName] = useState("");
  const [cloneOpen, setCloneOpen] = useState(false);

  const load = useCallback(async () => {
    if (!medicineId) return;
    setData(null);
    try {
      const res = await api.get(`/pharmacy/medicines/${medicineId}`);
      setData(res.data);
    } catch (e) { toast.error(apiError(e)); onClose(); }
  }, [medicineId, onClose]);

  useEffect(() => { setTab("overview"); load(); }, [load]);

  const m = data?.medicine;

  const submitMove = async (e) => {
    e.preventDefault();
    try {
      await api.post("/pharmacy/stock/move", {
        medicine_id: medicineId, direction: move, batch_id: form.batch_id,
        batch_no: form.batch_no, expiry_date: form.expiry_date,
        quantity: Number(form.quantity || 0), purchase_price: Number(form.purchase_price || 0),
        mrp: Number(form.mrp || 0), selling_price: Number(form.selling_price || 0),
        gst_percent: Number(form.gst_percent || 12), reason: form.reason });
      toast.success(move === "in" ? "Stock added and recorded." : "Stock taken out and recorded.");
      setMove(null); setForm(emptyMove()); load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  const act = async () => {
    try {
      if (confirm === "delete") {
        await api.delete(`/pharmacy/medicines/${medicineId}`, { params: { reason } });
        toast.success("Medicine deleted.");
        onChanged?.(); onClose();
      } else if (confirm === "archive") {
        const { data: res } = await api.post(`/pharmacy/medicines/${medicineId}/archive-batches`, {
          batch_ids: (data.batches || []).filter((b) => (b.quantity || 0) === 0).map((b) => b.batch_id),
          reason });
        toast.success(res.confirm || "Empty batches archived.");
        setConfirm(null); setReason(""); load(); onChanged?.();
      } else {
        await api.post(`/pharmacy/medicines/${medicineId}/status`, {
          status: m.status === "inactive" ? "active" : "inactive", reason });
        toast.success(m.status === "inactive" ? "Medicine reactivated." : "Medicine deactivated.");
        setConfirm(null); setReason(""); load(); onChanged?.();
      }
    } catch (err) { toast.error(apiError(err)); }
  };

  const clone = async (e) => {
    e.preventDefault();
    try {
      const { data: res } = await api.post(`/pharmacy/medicines/${medicineId}/clone`,
        { name: cloneName });
      toast.success(`Cloned as ${res.medicine.code} — ${res.medicine.name}`);
      setCloneOpen(false); setCloneName(""); onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Modal open={Boolean(medicineId)} onClose={onClose} wide
      title={m ? `${m.code} · ${m.name}` : "Medicine"}
      subtitle={m ? `${m.generic_name || m.brand || "—"} · stock ${m.stock ?? 0} ${m.unit || ""}` : ""}
      testid="medicine-detail-modal">
      {!data ? <Loading /> : (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {TABS.map(([v, l]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`medicine-tab-${v}`}
                className={`rounded-full px-4 py-2 text-sm font-medium ${tab === v
                  ? "bg-[#0F172A] text-white" : "border border-slate-300 text-slate-600 hover:border-slate-900"}`}>
                {l}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {can("pharmacy.medicines.edit") && (
              <button onClick={() => onEdit(m)} className={ghostBtnCls} data-testid="medicine-edit-btn">Edit</button>
            )}
            {can("pharmacy.stock.adjust") && (
              <>
                <button onClick={() => { setMove("in"); setForm(emptyMove()); }} className={primaryBtnCls}
                  data-testid="medicine-stock-in-btn">Stock In</button>
                <button onClick={() => { setMove("out"); setForm(emptyMove()); }} className={ghostBtnCls}
                  data-testid="medicine-stock-out-btn">Stock Out</button>
              </>
            )}
            {can("pharmacy.medicines.create") && (
              <button onClick={() => { setCloneName(`${m.name} (copy)`); setCloneOpen(true); }}
                className={ghostBtnCls} data-testid="medicine-clone-btn">Clone</button>
            )}
            {can("pharmacy.medicines.edit") && (
              <button onClick={() => { setConfirm("status"); setReason(""); }} className={ghostBtnCls}
                data-testid="medicine-toggle-btn">
                {m.status === "inactive" ? "Activate" : "Deactivate"}
              </button>
            )}
            {isAdmin && can("pharmacy.medicines.edit") && (data.can_delete ? (
              <button onClick={() => { setConfirm("delete"); setReason(""); }}
                data-testid="medicine-delete-btn"
                className="inline-flex items-center rounded-full border border-red-200 bg-white px-5 py-2 text-sm font-medium text-red-600 hover:border-red-500">
                Delete
              </button>
            ) : (
              <span className="self-center text-xs text-slate-400" data-testid="medicine-delete-blocked">
                Has transaction history — deactivate only
              </span>
            ))}
            {isAdmin && can("pharmacy.medicines.edit") && (data.batches || []).some((b) => (b.quantity || 0) === 0) && (
              <button onClick={() => { setConfirm("archive"); setReason(""); }} className={ghostBtnCls}
                data-testid="medicine-archive-btn">
                Archive empty batches
              </button>
            )}
          </div>

          {tab === "overview" && (
            <div className="space-y-6" data-testid="medicine-overview-panel">
              <div className="grid gap-5 sm:grid-cols-3">
                <Row label="Medicine code" value={m.code} />
                <Row label="Generic name" value={m.generic_name} />
                <Row label="Brand" value={m.brand} />
                <Row label="Barcode" value={m.barcode} />
                <Row label="HSN" value={m.hsn} />
                <Row label="GST" value={`${m.gst_percent ?? 0}%`} />
                <Row label="Purchase price" value={inr(m.purchase_price)} />
                <Row label="Selling price" value={inr(m.selling_price)} />
                <Row label="MRP" value={inr(m.mrp)} />
                <Row label="Current stock" value={`${m.stock ?? 0} ${m.unit || ""}`} />
                <Row label="Reorder / minimum stock" value={m.low_stock_limit} />
                <Row label="Rack" value={m.rack} />
                <Row label="Suppliers"
                  value={data.suppliers.length ? data.suppliers.map((s) => s.name).join(", ") : "None linked"} />
                <Row label="Status" value={m.status === "inactive" ? "Inactive" : "Active"} />
                <Row label="Total discount given"
                  value={inr(data.sales_history.reduce((s, r) => s + (r.discount || 0), 0))} />
              </div>
              {data.supplier_links.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr><th className="px-4 py-3">Supplier</th><th className="px-4 py-3">Supplier code</th>
                        <th className="px-4 py-3">Pack</th><th className="px-4 py-3 text-right">Rate</th>
                        <th className="px-4 py-3 text-right">Last rate</th><th className="px-4 py-3 text-right">MRP</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.supplier_links.map((l) => (
                        <tr key={l.supplier_id} data-testid={`medicine-supplier-link-${l.supplier_id}`}>
                          <td className="px-4 py-3 font-medium text-slate-900">{l.supplier_name}</td>
                          <td className="px-4 py-3 text-slate-600">{l.supplier_code || "—"}</td>
                          <td className="px-4 py-3 text-slate-600">{l.pack || "—"}</td>
                          <td className="px-4 py-3 text-right">{inr(l.purchase_price)}</td>
                          <td className="px-4 py-3 text-right text-slate-500">{inr(l.last_purchase_price)}</td>
                          <td className="px-4 py-3 text-right">{inr(l.mrp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "batches" && (
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="medicine-batches-panel">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="px-4 py-3">Batch</th><th className="px-4 py-3">Expiry</th>
                    <th className="px-4 py-3 text-right">Qty</th><th className="px-4 py-3 text-right">Purchase</th>
                    <th className="px-4 py-3 text-right">MRP</th><th className="px-4 py-3">Supplier</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.batches.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No batches yet.</td></tr>
                  )}
                  {data.batches.map((b) => (
                    <tr key={b.batch_id} data-testid={`medicine-batch-${b.batch_id}`}>
                      <td className="px-4 py-3 font-mono-num">{b.batch_no}</td>
                      <td className={`px-4 py-3 ${b.expiry_flag === "expired" ? "text-red-600"
                        : b.expiry_flag === "expiring" ? "text-amber-700" : "text-slate-600"}`}>
                        {dmy(b.expiry_date)}
                      </td>
                      <td className="px-4 py-3 text-right">{b.quantity}</td>
                      <td className="px-4 py-3 text-right">{inr(b.purchase_price)}</td>
                      <td className="px-4 py-3 text-right">{inr(b.mrp)}</td>
                      <td className="px-4 py-3 text-slate-600">{b.supplier_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === "purchases" && (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="medicine-purchases-panel">
              {data.purchase_history.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500">No purchases yet.</li>}
              {data.purchase_history.map((p) => (
                <li key={p.purchase_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                  <span>
                    <span className="font-mono-num font-medium">{p.purchase_no}</span>
                    <span className="ml-2 text-slate-500">{dmy(p.date)} · {p.supplier_name}</span>
                    <span className="ml-2 text-xs uppercase tracking-wider text-slate-400">{p.status === "draft" ? "Draft" : "Final"}</span>
                  </span>
                  <span className="text-slate-600">
                    {p.quantity}{p.free_quantity ? ` + ${p.free_quantity} free` : ""} @ {inr(p.purchase_price)}
                    {" "}· batch {p.batch_no} · {inr(p.line_total)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {tab === "sales" && (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" data-testid="medicine-sales-panel">
              {data.sales_history.length === 0 && <li className="px-4 py-6 text-center text-sm text-slate-500">No sales yet.</li>}
              {data.sales_history.map((s) => (
                <li key={s.sale_id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                  <span>
                    <span className="font-mono-num font-medium">{s.invoice_no}</span>
                    <span className="ml-2 text-slate-500">{dmy(s.date)} · {s.customer_name || "—"}</span>
                  </span>
                  <span className="text-slate-600">
                    {s.quantity} @ {inr(s.rate)} · batch {s.batch_no} · {inr(s.line_total)}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {tab === "timeline" && (
            <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="medicine-timeline-panel">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="px-4 py-3">Date &amp; time</th><th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3 text-right">Change</th><th className="px-4 py-3">Batch</th>
                    <th className="px-4 py-3">Reference / reason</th><th className="px-4 py-3">User</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.timeline.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">No movements recorded.</td></tr>
                  )}
                  {data.timeline.map((t) => (
                    <tr key={t.movement_id} data-testid={`medicine-movement-${t.movement_id}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-500">{dmyTime(t.created_at)}</td>
                      <td className="px-4 py-3 capitalize">{(t.type || "").replace(/_/g, " ")}</td>
                      <td className={`px-4 py-3 text-right font-semibold ${t.change < 0 ? "text-red-600" : "text-emerald-700"}`}>
                        {t.change > 0 ? `+${t.change}` : t.change}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{t.batch_no || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{t.reason || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{t.by_name || t.by || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Modal open={Boolean(move)} onClose={() => setMove(null)}
        title={move === "in" ? "Stock In" : "Stock Out"} subtitle={m?.name} testid="stock-move-modal">
        <form onSubmit={submitMove} className="grid gap-5 sm:grid-cols-2">
          <Field label={move === "out" ? "Batch *" : "Batch (blank = new batch)"}>
            <select className={inputCls} value={form.batch_id} data-testid="stock-move-batch-select"
              onChange={(e) => setForm({ ...form, batch_id: e.target.value })}>
              <option value="">{move === "out" ? "Select batch" : "New batch"}</option>
              {(data?.batches || []).map((b) => (
                <option key={b.batch_id} value={b.batch_id}>
                  {b.batch_no} · exp {dmy(b.expiry_date)} · {b.quantity} left
                </option>
              ))}
            </select>
          </Field>
          <Field label="Quantity *">
            <input required type="number" min="1" className={inputCls} value={form.quantity}
              data-testid="stock-move-qty-input" onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
          </Field>
          {move === "in" && !form.batch_id && (
            <>
              <Field label="New batch no. *">
                <input required className={inputCls} value={form.batch_no} data-testid="stock-move-batch-no"
                  onChange={(e) => setForm({ ...form, batch_no: e.target.value })} />
              </Field>
              <Field label="Expiry date *">
                <input required type="date" className={inputCls} value={form.expiry_date}
                  data-testid="stock-move-expiry" onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
              </Field>
              <Field label="MRP *">
                <input required type="number" min="0.01" step="0.01" className={inputCls} value={form.mrp}
                  data-testid="stock-move-mrp" onChange={(e) => setForm({ ...form, mrp: e.target.value })} />
              </Field>
              <Field label="Selling price">
                <input type="number" min="0" step="0.01" className={inputCls} value={form.selling_price}
                  data-testid="stock-move-selling" onChange={(e) => setForm({ ...form, selling_price: e.target.value })} />
              </Field>
              <Field label="Purchase price">
                <input type="number" min="0" step="0.01" className={inputCls} value={form.purchase_price}
                  onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} />
              </Field>
              <Field label="GST %">
                <input type="number" min="0" className={inputCls} value={form.gst_percent}
                  onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} />
              </Field>
            </>
          )}
          <Field label="Reason *" className="sm:col-span-2">
            <input required minLength={3} className={inputCls} value={form.reason} data-testid="stock-move-reason"
              placeholder={move === "in" ? "Opening stock, sample received, correction…" : "Damaged, expired, internal use…"}
              onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <button className={primaryBtnCls} data-testid="stock-move-submit-btn">
              {move === "in" ? "Add stock" : "Take stock out"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={cloneOpen} onClose={() => setCloneOpen(false)} title="Clone medicine"
        subtitle="A new medicine code is generated automatically." testid="medicine-clone-modal">
        <form onSubmit={clone} className="space-y-5">
          <Field label="New medicine name *">
            <input required className={inputCls} value={cloneName} data-testid="clone-name-input"
              onChange={(e) => setCloneName(e.target.value)} />
          </Field>
          <button className={primaryBtnCls} data-testid="clone-submit-btn">Create clone</button>
        </form>
      </Modal>

      <Modal open={Boolean(confirm)} onClose={() => setConfirm(null)}
        title={confirm === "delete" ? "Delete medicine permanently"
          : confirm === "archive" ? "Archive empty batches"
          : m?.status === "inactive" ? "Reactivate medicine" : "Deactivate medicine"}
        subtitle={m?.name} testid="medicine-confirm-modal">
        <form onSubmit={(e) => { e.preventDefault(); act(); }} className="space-y-5">
          <p className="text-sm text-slate-600">
            {confirm === "delete"
              ? "Permanent deletion is only possible when the medicine has never had stock, a purchase or a sale."
              : confirm === "archive"
                ? "Only batches at zero quantity are archived. History (purchases, sales, movements) is preserved and the batches stay queryable in the audit trail."
              : m?.status === "inactive"
                ? "The medicine will be sellable and searchable at the POS again."
                : "A deactivated medicine stays in all history but is hidden from POS search and new purchases."}
          </p>
          {!(confirm === "status" && m?.status === "inactive") && (
            <Field label="Reason * (recorded in the audit log)">
              <input required minLength={5} className={inputCls} value={reason}
                data-testid="medicine-reason-input" onChange={(e) => setReason(e.target.value)} />
            </Field>
          )}
          <button className={confirm === "delete"
            ? "inline-flex items-center rounded-full bg-red-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-red-700"
            : primaryBtnCls} data-testid="medicine-confirm-btn">
            {confirm === "delete" ? "Delete medicine" : confirm === "archive" ? "Archive batches"
              : m?.status === "inactive" ? "Activate" : "Deactivate"}
          </button>
        </form>
      </Modal>
    </Modal>
  );
}
